import React, { useEffect, useRef, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import {
  Button,
  ButtonLayout,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Grid,
  GridCol,
  Icon,
  Input,
  Loader,
  ReachDialog,
} from '../../ui-kit'
import { _, i18n } from '../../utils/intl'
import { cardsApi, holderAccountsApi, usersApi } from '../../api'
import { fieldToInputProps, fieldToNumberInputProps } from '../../utils/form/fieldToInputProps'

import { GetHolderInfoForm } from './GetHolderInfoForm'
import { SendCreditConfirmationForm } from './SendCreditConfirmationForm '
import { SendCreditForm } from './SendCreditForm'
import { appStore } from '../../AppStore'
import { formatMoney } from '../../utils/format'
import { get } from '../../utils/get'
import { limitTwoFloatingPoints } from '../../utils/forms/transformInput'
import { logSuccess } from '../../api/utils'
import { upAxios } from '../../../config'

interface Holder {
  account_number: number
  card_number: number
  first_name: string
  id: string
  last_name: string
  number: string
  balance: number
}

const HolderBox = styled.div`
  --height: 100px;

  flex: 1;
  height: var(--height);
  min-height: var(--height);
  padding-top: 10px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`

const WrapperNoPhone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`

const Errors = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-bottom: 24px;
`

const Error = styled.div`
  font-size: 16px;
`

const User = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`
const UserIcon = styled.div`
  width: 100%;
  margin-top: -10px;
  img {
    width: 100%;
    height: 60px;
    object-fit: contain;
  }
`
const UserName = styled.div`
  font-size: 1rem;
  line-height: 1.25;
  font-weight: 600;
  color: #f59100;

  @media screen and (min-width: 640px) {
    font-size: 1.25rem;
    line-height: 1.2;
  }
`

export const SEND_CREDIT_MODAL_ID = 'send-credit-modal'

export const SendCreditModal: React.FC<{
  cardId: string
  holderId: number
  holderAccountId: string
  onSuccess: () => void
  close: () => void
}> = observer(({ cardId, holderId, holderAccountId, onSuccess, close }) => {
  const currentUser = appStore.currentUser

  const cardNumberMinLength = 1

  const [getHolderForm] = useState(() => new GetHolderInfoForm())
  const [sendCreditForm] = useState(() => new SendCreditForm())
  const [confirmationForm] = useState(() => new SendCreditConfirmationForm())

  const [fetchingBalance, setFetchingBalance] = useState(false)
  const [fetchingHolder, setFetchingHolder] = useState(false)
  const [sending, setSending] = useState(false)
  const [smsCodeSent, setSmsCodeSent] = useState(false)

  const [errors, setErrors] = useState<string[]>()
  const [balance, setBalance] = useState<number>()
  const [holder, setHolder] = useState<Holder | { errorMessages?: string[] }>()
  const [isHolderConfirmed, setIsHolderConfirmed] = useState(false)

  const [confirmationCountdownSec, setConfirmationCountdownSec] = useState<number>(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const setTranslatedErrors = (messages: string[] = []) => {
    const translated = messages.map(msg => i18n._(msg))
    setErrors(translated)
  }

  useEffect(() => {
    if (confirmationCountdownSec > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setConfirmationCountdownSec(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current!)
            timerRef.current = null
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [confirmationCountdownSec])

  const getBalance = async () => {
    setFetchingBalance(true)
    try {
      const cardBalanceResponse = await holderAccountsApi.getHolderAccountBalance(holderAccountId)
      setBalance(cardBalanceResponse.data)
    } finally {
      setFetchingBalance(false)
    }
  }

  const getHolderInfo = async () => {
    const { hasError } = await getHolderForm.form.validate()
    if (hasError) {
      setIsHolderConfirmed(false)
      return console.warn(`form has errors`, getHolderForm.form.formError)
    }
    const valid = getHolderForm.serialize()
    setFetchingHolder(true)
    try {
      const holderResponse = await upAxios.post(
        `/employees/find?holder_id=${Number(valid.receiverId)}`
      )
      const parsedHolder: Holder = { ...holderResponse.data }
      if (parsedHolder) {
        setHolder(parsedHolder)
        setIsHolderConfirmed(true)
      } else {
        setHolder({ errorMessages: ['Zadaného držiteľa karty sa nepodarilo nájsť'] })
        setIsHolderConfirmed(false)
      }
    } catch (_err) {
      setIsHolderConfirmed(false)
      const err = _err as any
      const errMessages = get(err.response, 'data', 'messages')

      if (errMessages && errMessages.length > 0) {
        setHolder({ errorMessages: errMessages?.map((e: any) => e.message) })
      } else {
        setHolder({ errorMessages: ['Zadaného držiteľa karty sa nepodarilo nájsť'] })
      }
    } finally {
      setFetchingHolder(false)
    }
  }

  const hasValidInputs = async () => {
    const receiverIdValue = receiverId?.value ? `${receiverId.value}` : ''
    if (
      !receiverIdValue ||
      receiverIdValue.length < cardNumberMinLength ||
      !isHolderConfirmed ||
      !(holder as Holder)?.account_number
    ) {
      const holderError = 'Pred odoslaním kreditu potvrďte držiteľa stravovacej karty Up'

      console.warn(`form has errors`, holderError)
      getHolderForm.fields.receiverId.setError(holderError)
      return false
    }

    const { hasError: hasErrorHolder } = await getHolderForm.form.validate()
    if (hasErrorHolder) {
      return console.warn(`form has errors`, getHolderForm.form.formError)
    }

    const fromHolderId = holderId
    const toHolderId = Number((holder as Holder)?.account_number)

    if (fromHolderId === toHolderId) {
      const holderError = 'Odoslanie kreditu na vlastný účet nie je povolené.'

      console.warn(`form has errors`, holderError)
      getHolderForm.fields.receiverId.setError(holderError)
      return false
    }

    const { hasError: hasErrorCredit } = await sendCreditForm.form.validate()
    if (hasErrorCredit) {
      console.warn(`form has errors`, sendCreditForm.form.formError)
      return false
    }

    const validCredit = sendCreditForm.serialize()
    if (!balance || Number(validCredit.amount) > balance) {
      const balanceError = 'Na účte nie je dostatok kreditov na vykonanie transakcie.'

      console.warn(`form has errors`, balanceError)
      sendCreditForm.fields.amount.setError(balanceError)
      return false
    }
    return true
  }

  const requestSMSCode = async () => {
    if (!currentUser?.id) {
      console.error('Current user id not found', currentUser)
      return
    }
    if (await hasValidInputs()) {
      setSending(true)
      try {
        const validCredit = sendCreditForm.serialize()
        const amount = Number(validCredit.amount.replace(',', '.'))
        const recipient_holder_id = Number((holder as Holder)?.account_number)

        /* const response =  */ await usersApi.requestConfirmationCode(currentUser?.id, {
          amount,
          recipient_holder_id,
        })

        // console.log(response)

        setConfirmationCountdownSec(60 * 5) // TODO - THE TIMEOUT WILL BE TAKEN FROM THE CONFIRMATION CODE ABOVE
        setSmsCodeSent(true)
        logSuccess(
          _(
            t`Prijali sme vašu požiadavku na spracovanie, na vaše číslo bol odoslaný SMS kód na potvrdenie.`
          )
        )
      } catch (_err) {
        setSmsCodeSent(false)
        const err = _err as any
        const errMessages = get(err.response, 'data', 'messages')
        if (errMessages && errMessages.length > 0) {
          setTranslatedErrors(errMessages?.map((e: any) => e.message))
        } else {
          setTranslatedErrors(['Pri odosielaní SMS kódu na potvrdenie došlo k chybe.'])
        }
      } finally {
        setSending(false)
      }
    }
  }

  const sendCredit = async () => {
    if (await hasValidInputs()) {
      const { hasError: hasErrorConfirmation } = await confirmationForm.form.validate()
      if (hasErrorConfirmation) {
        console.warn(`form has errors`, confirmationForm.form.formError)
        return false
      }

      const validConfirmation = confirmationForm.serialize()
      const validCredit = sendCreditForm.serialize()

      setSending(true)
      try {
        await cardsApi.postSendAmount(cardId, {
          user_id: currentUser?.id as string,
          recipient_holder_id: Number((holder as Holder)?.account_number),
          amount: Number(validCredit.amount.replace(',', '.')),
          phone_code: validConfirmation.smsCode,
        })
        logSuccess(_(t`Poslanie kreditu bolo úspešné`))
        onSuccess()
        close()
      } catch (_err) {
        const err = _err as any
        const errMessages = get(err.response, 'data', 'messages')
        if (errMessages && errMessages.length > 0) {
          setTranslatedErrors(errMessages?.map((e: any) => e.message))
        } else {
          setTranslatedErrors(['Pri transakcii došlo k chybe.'])
        }
      } finally {
        setSending(false)
      }
    }
  }

  const formatTime = (sec: number) => {
    const minutes = Math.floor(sec / 60)
    const seconds = sec % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  const { amount } = sendCreditForm.fields
  const { receiverId } = getHolderForm.fields
  const { smsCode } = confirmationForm.fields
  const receiverIdValue = receiverId?.value ? `${receiverId.value}` : ''
  const canSearchHolder =
    receiverIdValue.length >= cardNumberMinLength && !smsCodeSent && !fetchingHolder

  //
  // AUTO-FETCH
  //

  // On Init
  useEffect(() => {
    getBalance()
  }, [])

  // Reset looked up holder on receiver id change
  useEffect(() => {
    setIsHolderConfirmed(false)
    setHolder(undefined)
  }, [receiverId?.value])

  //
  // RENDER RECEIVER
  //
  const renderReceiver = () => {
    if (fetchingHolder) {
      return <Loader size="large" />
    }

    if ((holder as any)?.errorMessages) {
      return (
        <div>
          {(holder as any).errorMessages.map((errorMessage: string, i: number) => (
            <div key={i} className="form-message--error">
              {i18n._(errorMessage)}
            </div>
          ))}
        </div>
      )
    }

    if ((holder as Holder)?.account_number) {
      return (
        <User>
          <UserIcon>
            <img src="/static/illustrations/user.svg" alt="App Store" />
          </UserIcon>
          <UserName>
            {(holder as Holder)?.last_name} {(holder as Holder)?.first_name}
          </UserName>
        </User>
      )
    }

    return <img className="flex align-self-center" src="/static/img/card_id.png" alt={_(t`card`)} />
  }

  //
  // RENDER
  //
  return (
    <ReachDialog id={SEND_CREDIT_MODAL_ID} onDismiss={close}>
      <>
        <DialogHeader title={_(t`Poslať kredit`)} />
        {currentUser?.phone?.length ? (
          <>
            <DialogContent className="mb-none">
              <h2 className="h5 text-color-grey-600">
                <Trans>Príjemca</Trans>
              </h2>

              <Grid>
                <GridCol className="mb-tiny">
                  <p className="text-color-grey-600">
                    <Trans>
                      Zadajte, prosím, číslo držitela stravovacej karty UpDéjeuner. Číslo nájdete na
                      druhej strane karty pod čiarovým kódom.
                    </Trans>
                  </p>
                </GridCol>
                <GridCol>
                  <Input
                    id="receiver-id"
                    label={<Trans>Id príjemcu</Trans>}
                    data-testid="recipient"
                    disabled={smsCodeSent}
                    {...fieldToInputProps(receiverId, value =>
                      `${value}`.replace(/\D+/g, '').slice(0, 12)
                    )}
                  />

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      type="primary"
                      onClick={getHolderInfo}
                      isDisabled={!canSearchHolder}
                      isLoading={fetchingHolder}
                      data-testid="search-recipient">
                      <Trans>Vyhľadať držiteľa</Trans>
                    </Button>
                  </div>
                </GridCol>
              </Grid>

              <HolderBox>{renderReceiver()}</HolderBox>

              <h2 className="h5 text-color-grey-600">
                <Trans>Kredit</Trans>
              </h2>

              <Grid>
                <GridCol className="mb-tiny">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span className="text-color-grey-600">
                      <Trans>Váš zostatok</Trans>:
                    </span>{' '}
                    {fetchingBalance ? (
                      <span>
                        <Loader size="small" />
                      </span>
                    ) : (
                      <strong>{balance && formatMoney(balance as any)} €</strong>
                    )}
                  </div>
                </GridCol>
                <GridCol className="mb-tiny">
                  <Input
                    id="amount"
                    type="number"
                    label={<Trans>Suma</Trans>}
                    rightAddons={<Icon name="euro" size="medium" />}
                    data-testid="amount"
                    disabled={smsCodeSent}
                    {...fieldToNumberInputProps(amount, limitTwoFloatingPoints)}
                  />
                </GridCol>
              </Grid>
            </DialogContent>

            <DialogFooter>
              {smsCodeSent ? (
                <>
                  <Grid>
                    <GridCol className="mb-tiny">
                      <p className="text-color-grey-600">
                        <Trans>
                          Na vaše telefónne číslo bol odoslaný SMS kód na potvrdenie transakcie,
                          zadajte ho prosím nižšie:
                        </Trans>
                      </p>
                    </GridCol>

                    <GridCol className="mb-large">
                      <Input
                        id="sms-code"
                        label={<Trans>SMS Kód</Trans>}
                        data-testid="sms-code"
                        {...fieldToInputProps(smsCode)}
                      />

                      <ButtonLayout direction="horizontal" className="align-items-right">
                        <Button
                          onClick={requestSMSCode}
                          isDisabled={!!confirmationCountdownSec && confirmationCountdownSec > 0}
                          style={{ marginLeft: 'auto' }}
                          data-testid="request-new-confirmation-code">
                          <Trans>Poslať nový SMS kód</Trans>
                          {confirmationCountdownSec > 0
                            ? ` (${formatTime(confirmationCountdownSec)})`
                            : ''}
                        </Button>
                      </ButtonLayout>
                    </GridCol>
                  </Grid>
                </>
              ) : null}

              {errors?.length ? (
                <Errors>
                  {errors?.map((err, i) => (
                    <Error key={i} className="form-message--error">
                      {err}
                    </Error>
                  ))}
                </Errors>
              ) : null}

              <ButtonLayout direction="horizontal" className="align-items-right">
                <Button onClick={close}>
                  <Trans>Zatvoriť</Trans>
                </Button>
                <Button
                  type="primary"
                  onClick={smsCodeSent ? sendCredit : requestSMSCode}
                  style={{ alignSelf: 'flex-end' }}
                  isDisabled={sending}
                  isLoading={sending}
                  data-testid="submit">
                  <Trans>{smsCodeSent ? <>Potvrdiť transakciu</> : <>Poslať kredit</>}</Trans>
                </Button>
              </ButtonLayout>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogContent className="mb-none">
              <WrapperNoPhone>
                <p className="text-color-grey-600">
                  <Trans>
                    Na odoslanie kreditu je potrebné mať v profile uvedené telefónne číslo, na ktoré
                    vám príde potvrdzovací SMS kód.
                  </Trans>
                </p>
                <Button
                  style={{ alignSelf: 'center' }}
                  type="primary"
                  onClick={() => {
                    location.pathname = '/profile'
                  }}>
                  <Trans>Pridať telefónne číslo</Trans>
                </Button>
              </WrapperNoPhone>
            </DialogContent>
          </>
        )}
      </>
    </ReachDialog>
  )
})

SendCreditModal.displayName = 'SendCreditModal'
